import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import { RestApiError, restClient } from "lib/rest-client";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { getContractStatusBadge } from "routes/crm/utils";
import { formatDate } from "utils/relative-time";

type ContractWithTalent = {
  id: string;
  talentId: string;
  talentName: string;
  clientCompany: string | null;
  projectName: string | null;
  contractType: string;
  reward: number | null;
  margin: number | null;
  startDate: string;
  endDate: string | null;
  status: string;
  renewalCount: number;
  createdAt: string;
};

type ContractListResponse = {
  items: ContractWithTalent[];
  total: number;
  page: number;
  limit: number;
};

/**
 * 残日数を計算する
 * @param {string | null} endDate 契約終了日
 * @return {number | null} 残日数（nullは終了日未設定）
 */
function calcDaysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * 残日数に応じたテキストクラスを返す
 * @param {number | null} days 残日数
 * @return {string} CSSクラス
 */
function getDaysRemainingClass(days: number | null): string {
  if (days === null) return "text-base-content/60";
  if (days <= 14) return "text-error font-bold";
  if (days <= 30) return "text-warning font-bold";
  return "text-success";
}

/**
 * 契約ステータスのラベルを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */

type ContractTabProps = {
  canManageContracts: boolean;
};

/**
 * 契約タブコンポーネント
 * 全タレント横断の契約一覧をAPIから取得して表示する
 * @param {object} props プロパティ
 * @param {boolean} props.canManageContracts 契約管理権限（falseの場合、単価・マージンをマスクする）
 * @return {React.ReactElement} 契約タブ
 */
export const ContractTab: React.FC<ContractTabProps> = ({
  canManageContracts,
}) => {
  const [contracts, setContracts] = useState<ContractWithTalent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    let cancelled = false;

    const fetchContracts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {
          page: String(page),
          limit: String(limit),
        };
        if (statusFilter) {
          params.status = statusFilter;
        }
        const res = await restClient.get<ContractListResponse>(
          "/talents/contracts",
          params,
        );
        if (!cancelled) {
          setContracts(res.items);
          setTotal(res.total);
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e instanceof RestApiError
              ? e.message
              : "契約データの取得に失敗しました";
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchContracts().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter]);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const totalPages = Math.ceil(total / limit);

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <select
          className="select select-bordered select-sm"
          value={statusFilter}
          onChange={e => handleStatusFilterChange(e.target.value)}
        >
          <option value="">全ステータス</option>
          <option value="active">稼働中</option>
          <option value="renewal_pending">更新待ち</option>
          <option value="ended">終了</option>
          <option value="renewed">更新済</option>
        </select>
        <span className="text-sm text-base-content/60">{total}件</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>タレント</Table.HeaderCell>
              <Table.HeaderCell>契約先</Table.HeaderCell>
              <Table.HeaderCell>案件名</Table.HeaderCell>
              <Table.HeaderCell>単価</Table.HeaderCell>
              <Table.HeaderCell>マージン</Table.HeaderCell>
              <Table.HeaderCell>契約期間</Table.HeaderCell>
              <Table.HeaderCell>残日数</Table.HeaderCell>
              <Table.HeaderCell>ステータス</Table.HeaderCell>
              <Table.HeaderCell>更新回数</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {contracts.map(contract => {
              const daysRemaining = calcDaysRemaining(contract.endDate);
              const statusBadge = getContractStatusBadge(contract.status);
              const daysClass = getDaysRemainingClass(daysRemaining);
              return (
                <Table.Row key={contract.id}>
                  <Table.Cell>
                    <span className="font-medium">{contract.talentName}</span>
                  </Table.Cell>
                  <Table.Cell>{contract.clientCompany || "-"}</Table.Cell>
                  <Table.Cell>{contract.projectName || "-"}</Table.Cell>
                  <Table.Cell>
                    {canManageContracts
                      ? contract.reward != null
                        ? `${contract.reward}万`
                        : "-"
                      : "---"}
                  </Table.Cell>
                  <Table.Cell>
                    {canManageContracts
                      ? contract.margin != null
                        ? `${contract.margin}万`
                        : "-"
                      : "---"}
                  </Table.Cell>
                  <Table.Cell>
                    {formatDate(contract.startDate)}
                    {contract.endDate
                      ? ` ~ ${formatDate(contract.endDate)}`
                      : " ~"}
                  </Table.Cell>
                  <Table.Cell>
                    <span className={daysClass}>
                      {daysRemaining !== null ? `${daysRemaining}日` : "-"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone={statusBadge.tone}>
                      {statusBadge.label}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>{contract.renewalCount}</Table.Cell>
                </Table.Row>
              );
            })}
            {contracts.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="text-center py-8 text-base-content/60"
                >
                  契約が見つかりません
                </td>
              </tr>
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <div className="join">
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled
            >
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              variant="neutral"
              size="sm"
              isJoinItem
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
